// The bell: an unread count, and a dropdown of what is waiting.
//
// It sits in the operator shell's header and in the client booking header. Same
// component both times — a second copy is how the two silently stop matching,
// the same reason TenantBooking serves both booking doors.

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Bell, Check } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import { useRole } from '@/hooks/useRole'
import type { NotificationRecord } from '@/lib/api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// Where a notification leads. The same mapping the push payload carries, and
// deliberately duplicated rather than shared: the server builds a URL for a
// service worker that has no route table, and this one is inside the router
// where relative navigation is what works. See /audit Pass 8.
//
// EVERY destination here is an OPERATOR screen, which is why the second argument
// is REQUIRED rather than defaulted. The same bell hangs in the client booking
// header, and a client sent to /booking/:id lands on a screen their role cannot
// open — a notification that navigates to a permission wall is worse than one
// that only clears itself. Making the caller state which side of the line it is
// on is the same reasoning as `asCustomer` on earliestBookableStart: a default
// would silently give the wrong audience the lenient answer.
//
// null is a complete answer for a client: the body already carries the service
// and the time, and there is no per-booking screen on the customer surface to
// send them to.
export function notificationHref(
  n: NotificationRecord,
  canOpenOperatorScreens: boolean,
): string | null {
  if (!canOpenOperatorScreens) return null
  switch (n.entity_type) {
    case 'booking': return n.entity_id ? `/booking/${n.entity_id}` : '/schedule'
    case 'conversation': return n.entity_id
      ? `/conversation/${n.entity_id}${n.anchor_id ? `?message=${encodeURIComponent(n.anchor_id)}` : ''}`
      : '/inbox'
    case 'payment': return '/payments'
    case 'customer': return n.entity_id ? `/customer/${n.entity_id}` : '/customers'
    default: return null
  }
}

/** '2026-09-04T14:30:00' → 'just now' / '12m' / '3h' / 'Wed'. */
export function relativeAge(created: string, now: Date): string {
  // API datetimes are tenant-local naive strings, so this parses the PARTS
  // rather than constructing a Date from the string — new Date('2026-09-04T14:30')
  // reads it in the BROWSER's timezone and an operator abroad sees every
  // notification hours out. Same rule as lib/format.ts.
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(created)
  if (!m) return ''
  const then = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0))
  const nowUtc = Date.UTC(
    now.getFullYear(), now.getMonth(), now.getDate(),
    now.getHours(), now.getMinutes(), now.getSeconds(),
  )
  const mins = Math.floor((nowUtc - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return days < 7 ? `${days}d` : ''
}

export function NotificationBell({ className }: { className?: string }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Slow: the bell is ambient. Opening it refreshes, so the interval only has to
  // catch the case where nobody is looking.
  const { items, unread, markRead, dismiss, dismissAll, refresh } = useNotifications(60000)
  // The same gate the calendar quick actions and @-mentions use. A client sees
  // their own notifications in this same bell; only an operator can be sent to
  // the screens they point at.
  const { userRole } = useRole()
  const canOpen = userRole === 'owner' || userRole === 'staff'

  const now = useMemo(() => new Date(), [open, items.length])

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) return
    void refresh()
    // Opening the bell is what READS them — that is what stops the badge
    // nagging. It deliberately does NOT dismiss: the list has to stay readable
    // while it is being read, which is why read and dismissed are two states
    // rather than one.
    const unreadIds = items.filter((n) => !n.read_at).map((n) => n.id)
    if (unreadIds.length > 0) void markRead(unreadIds)
  }, [items, markRead, refresh])

  const openItem = useCallback((n: NotificationRecord) => {
    const href = notificationHref(n, canOpen)
    setOpen(false)
    // Acting on it dismisses it — the operator has dealt with it. A notification
    // that stays after you have opened the thing it is about is clutter.
    void dismiss([n.id])
    if (href) navigate(href)
  }, [canOpen, dismiss, navigate])

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground '
            + 'hover:bg-muted hover:text-foreground ' + (className ?? '')
          }
          // The count is IN the label, not only in the badge: a screen reader
          // announcing "Notifications" would say nothing about the five waiting.
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Bell size={18} aria-hidden />
          {unread > 0 ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 flex min-w-[17px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-[17px] text-white"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[330px] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[13px] font-semibold">Notifications</span>
          {items.length > 0 ? (
            <button
              type="button"
              onClick={() => { void dismissAll(); setOpen(false) }}
              className="text-[12px] font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              Dismiss all
            </button>
          ) : null}
        </div>

        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
            Nothing new. You are all caught up.
          </p>
        ) : (
          <ul className="max-h-[min(420px,60vh)] overflow-y-auto">
            {items.map((n) => (
              <li key={n.id} className="border-b border-border last:border-b-0">
                <div className="flex items-start gap-2 px-3 py-2.5 hover:bg-muted/60">
                  <button
                    type="button"
                    onClick={() => openItem(n)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-[13px] font-semibold">{n.title}</span>
                      <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                        {relativeAge(n.created_at, now)}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted-foreground">{n.body}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { void dismiss([n.id]) }}
                    aria-label={`Dismiss: ${n.title}`}
                    title="Dismiss"
                    className="mt-0.5 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Check size={14} aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
